import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import EntryList from "../components/EntryList";

const PAGE_SIZE = 10;

export default function EntryListPage({ onSelect }) {
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [medicineType, setMedicineType] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const res = await client.get("/entries");
        setEntries(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/login");
        }
      }
    };
    fetchEntries();
  }, [navigate]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await client.get("/entries/search", {
          params: { q: searchQuery },
        });
        setSearchResults(res.data);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Unified filter logic used for both main list and search results
  const applyFilter = (data) => {
    if (medicineType === "all") return data;
    return data.filter((entry) => {
      const type = (entry.medicine_type || entry.medicinetype || "").toLowerCase().trim();
      return type === medicineType.toLowerCase();
    });
  };

  const isInSearch = searchQuery.trim() !== "";
  
  // Apply filter to the active dataset
  const visibleEntries = useMemo(() => {
    return isInSearch ? applyFilter(searchResults) : applyFilter(entries);
  }, [entries, searchResults, medicineType, isInSearch]);

  const totalPages = Math.ceil(visibleEntries.length / PAGE_SIZE);
  const paginated = visibleEntries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <main className="EntryListPage">
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "12px",
          }}
        >
          {/* LEFT: Search + Filters */}
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: "220px" }}>
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "9px 14px",
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
                fontSize: "14px",
                marginBottom: "8px",
                maxWidth: "380px",
              }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: 'wrap', marginBottom: "20px" }}>
              <span style={{ fontSize: "14px", opacity: 0.75, whiteSpace: "nowrap" }}>Filter by:</span>
              {[
                { value: "all", label: "🌿🌀 All" },
                { value: "cannabis", label: "🌿 Cannabis" },
                { value: "mushroom", label: "🌀 Psychedelics" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setMedicineType(opt.value);
                    setPage(1);
                  }}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    border: "1px solid rgba(125,211,232,0.3)",
                    background: medicineType === opt.value ? "rgba(125,211,232,0.2)" : "transparent",
                    color: medicineType === opt.value ? "#7dd3e8" : "rgba(255,255,255,0.7)",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: medicineType === opt.value ? 600 : 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT: Action Buttons */}
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            
            <button onClick={() => navigate("/new")}>New Entry</button>
          </div>
        </div>
      </div>

      {isSearching && (
        <p style={{ opacity: 0.5, fontSize: "13px", marginBottom: "12px" }}>Searching...</p>
      )}

      <EntryList entries={paginated} onSelect={onSelect} />

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "24px", fontSize: "13px", opacity: 0.7 }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ background: "none", border: "none", color: "inherit", cursor: page === 1 ? "default" : "pointer", opacity: page === 1 ? 0.3 : 1, fontSize: "18px" }}
          >
            ‹
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ background: "none", border: "none", color: "inherit", cursor: page === totalPages ? "default" : "pointer", opacity: page === totalPages ? 0.3 : 1, fontSize: "18px" }}
          >
            ›
          </button>
        </div>
      )}
    </main>
  );
}